import { Helmet } from "react-helmet-async";

// ===============================
// ORGANIZATION SCHEMA
// ===============================
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Groomie Roomie",
    "legalName": "Groomie Roomie Mobile Pet Grooming LLC",
    "url": "https://groomieroomie.com",
    "logo": "https://groomieroomie.com/logo.png",
    "description": "Premium mobile dog and cat grooming services serving Denver and surrounding neighborhoods.",
    "foundingDate": "2020",
    "founders": [{
      "@type": "Person",
      "name": "Groomie Roomie Team"
    }],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2145 S Newport St",
      "addressLocality": "Denver",
      "addressRegion": "CO",
      "postalCode": "80224",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-720-243-2853",
      "contactType": "customer service",
      "areaServed": "US",
      "availableLanguage": ["English", "Spanish"]
    },
    "sameAs": [
      "https://www.instagram.com/groomie_roomie_denver_",
      "https://www.tiktok.com/@groomie_roomie_denver",
      "https://www.facebook.com/groomieroomie"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// LOCAL BUSINESS SCHEMA (Enhanced for AI Search Engines)
// ===============================
interface LocalBusinessSchemaProps {
  type?: "LocalBusiness" | "PetStore" | "PetService";
  name?: string;
  description?: string;
  areaServed?: Array<{ type: string; name: string; containedInPlace?: string }>;
  geo?: { lat: number; lng: number };
}

export const LocalBusinessSchema = ({ 
  type = "LocalBusiness",
  name = "Groomie Roomie Mobile Pet Grooming",
  description = "Premium mobile dog and cat grooming services in Denver. Luxury spa-level grooming delivered to your doorstep across 24+ neighborhoods. No stressful car rides or kennel anxiety.",
  areaServed,
  geo
}: LocalBusinessSchemaProps) => {
  const defaultAreas = [
    { "@type": "City", "name": "Denver", "containedInPlace": { "@type": "State", "name": "Colorado" } },
    { "@type": "Neighborhood", "name": "Cherry Creek", "containedInPlace": "Denver, CO" },
    { "@type": "Neighborhood", "name": "Capitol Hill", "containedInPlace": "Denver, CO" },
    { "@type": "City", "name": "Highlands Ranch" },
    { "@type": "City", "name": "Aurora" },
    { "@type": "City", "name": "Lakewood" },
    { "@type": "City", "name": "Littleton" },
    { "@type": "Neighborhood", "name": "Washington Park", "containedInPlace": "Denver, CO" },
    { "@type": "Neighborhood", "name": "DTC", "containedInPlace": "Denver, CO" },
    { "@type": "Neighborhood", "name": "RiNo", "containedInPlace": "Denver, CO" },
    { "@type": "City", "name": "Greenwood Village" },
    { "@type": "City", "name": "Arvada" },
    { "@type": "City", "name": "Centennial" },
    { "@type": "City", "name": "Englewood" },
    { "@type": "City", "name": "Golden" },
    { "@type": "City", "name": "Wheat Ridge" }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    "additionalType": [
      "https://www.productontology.org/id/Dog_grooming",
      "https://www.productontology.org/id/Pet_grooming"
    ],
    "name": name,
    "alternateName": "Groomie Roomie",
    "description": description,
    "image": [
      "https://groomieroomie.com/og-image.jpg",
      "https://groomieroomie.com/logo.png"
    ],
    "url": "https://groomieroomie.com",
    "telephone": "+1-720-243-2853",
    "email": "groomieroomie@gmail.com",
    "priceRange": "$120-$200",
    "paymentAccepted": "Cash, Credit Card, Debit Card, Venmo, Zelle",
    "currenciesAccepted": "USD",
    "serviceType": "Mobile Dog & Cat Grooming",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2145 S Newport St",
      "addressLocality": "Denver",
      "addressRegion": "CO",
      "postalCode": "80224",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo?.lat || 39.6767,
      "longitude": geo?.lng || -104.9262
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Mobile Pet Grooming Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Basic Grooming Package",
            "description": "Bath, brush, nail trim, ear cleaning"
          },
          "price": "120",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Premium Grooming Package",
            "description": "Full grooming with haircut and styling"
          },
          "priceRange": "$150-$175",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Luxury Spa Package",
            "description": "Complete spa experience with specialty treatments"
          },
          "price": "200",
          "priceCurrency": "USD"
        }
      ]
    },
    "areaServed": areaServed || defaultAreas,
    "knowsAbout": [
      "Mobile Dog Grooming",
      "Mobile Cat Grooming",
      "Pet Grooming",
      "Dog Bathing",
      "Cat Bathing",
      "Nail Trimming",
      "De-shedding Treatment",
      "Teeth Cleaning",
      "Ear Cleaning",
      "Breed-Specific Grooming"
    ],
    "sameAs": [
      "https://www.instagram.com/groomie_roomie_denver_",
      "https://www.tiktok.com/@groomie_roomie_denver",
      "https://www.facebook.com/groomieroomie"
    ],
    "slogan": "Luxury Mobile Pet Grooming - We Come to You"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// SERVICE SCHEMA (Enhanced with AggregateOffer)
// ===============================
interface ServiceSchemaProps {
  name: string;
  description: string;
  price: number;
  priceMax?: number;
  priceRange?: string;
  duration?: string;
  image?: string;
  serviceType?: string;
  category?: string;
  additionalType?: string;
  termsOfService?: string;
  canonicalUrl?: string;
  validFrom?: string;
  audience?: {
    type: string;
    audienceType: string;
  };
  includeFullAddress?: boolean;
}

export const ServiceSchema = ({ 
  name, 
  description, 
  price, 
  priceMax,
  priceRange,
  duration,
  image,
  serviceType = "PetGroomingService",
  category,
  additionalType,
  termsOfService,
  canonicalUrl,
  validFrom,
  audience,
  includeFullAddress = false
}: ServiceSchemaProps) => {
  const provider = includeFullAddress ? {
    "@type": "LocalBusiness",
    "name": "Groomie Roomie Mobile Pet Grooming",
    "url": "https://groomieroomie.com",
    "telephone": "+1-720-243-2853",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2145 S Newport St",
      "addressLocality": "Denver",
      "addressRegion": "CO",
      "postalCode": "80224",
      "addressCountry": "US"
    }
  } : {
    "@type": "LocalBusiness",
    "name": "Groomie Roomie Mobile Pet Grooming",
    "url": "https://groomieroomie.com",
    "telephone": "+1-720-243-2853"
  };

  const areaServed = includeFullAddress ? [
    {
      "@type": "City",
      "name": "Denver",
      "containedInPlace": {
        "@type": "State",
        "name": "Colorado"
      }
    }
  ] : {
    "@type": "City",
    "name": "Denver",
    "sameAs": "https://en.wikipedia.org/wiki/Denver"
  };

  const offers: Record<string, unknown> = {
    "@type": "Offer",
    "priceCurrency": "USD",
    "price": ((price + (priceMax || price)) / 2).toFixed(2),
    "availability": "https://schema.org/InStock"
  };

  if (priceRange) {
    offers.priceRange = priceRange;
  }
  if (canonicalUrl) {
    offers.url = canonicalUrl;
  }
  if (validFrom) {
    offers.validFrom = validFrom;
  }

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceType,
    "name": name,
    "description": description,
    "provider": provider,
    "areaServed": areaServed,
    "offers": offers
  };

  if (category) {
    schema.category = category;
  }
  if (additionalType) {
    schema.additionalType = additionalType;
  }
  if (termsOfService) {
    schema.termsOfService = termsOfService;
  }
  if (duration) {
    schema.duration = duration;
  }
  if (image) {
    schema.image = image;
  }
  if (audience) {
    schema.audience = {
      "@type": audience.type,
      "audienceType": audience.audienceType
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// SERVICES LIST SCHEMA (for Services page)
// ===============================
interface ServiceItem {
  name: string;
  description: string;
  price: string;
  url: string;
}

interface ServicesListSchemaProps {
  services: ServiceItem[];
}

export const ServicesListSchema = ({ services }: ServicesListSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Groomie Roomie Mobile Pet Grooming Services",
    "description": "Professional mobile dog and cat grooming services in Denver",
    "numberOfItems": services.length,
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.name,
        "description": service.description,
        "offers": {
          "@type": "Offer",
          "price": service.price,
          "priceCurrency": "USD"
        },
        "url": `https://groomieroomie.com${service.url}`
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// BREADCRUMB SCHEMA
// ===============================
interface BreadcrumbSchemaProps {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => {
      const fullUrl = item.url.startsWith("http") ? item.url : `https://groomieroomie.com${item.url}`;
      return {
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": {
          "@type": "WebPage",
          "@id": fullUrl
        }
      };
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// FAQ SCHEMA
// ===============================
interface FAQSchemaProps {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export const FAQSchema = ({ questions }: FAQSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// REVIEW SCHEMA (Single)
// ===============================
interface ReviewSchemaProps {
  author: string;
  rating: number;
  reviewBody: string;
  datePublished: string;
  location?: string;
}

export const ReviewSchema = ({ author, rating, reviewBody, datePublished, location }: ReviewSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "LocalBusiness",
      "name": "Groomie Roomie",
      "image": "https://groomieroomie.com/og-image.jpg"
    },
    "author": {
      "@type": "Person",
      "name": author
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": rating,
      "bestRating": "5",
      "worstRating": "1"
    },
    "reviewBody": reviewBody,
    "datePublished": datePublished,
    ...(location && { "locationCreated": { "@type": "Place", "name": location } })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// AGGREGATE REVIEWS SCHEMA
// ===============================
interface AggregateReviewsSchemaProps {
  reviews: Array<{
    author: string;
    rating: number;
    reviewBody: string;
    datePublished: string;
    location?: string;
  }>;
}

export const AggregateReviewsSchema = ({ reviews }: AggregateReviewsSchemaProps) => {
  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Groomie Roomie Mobile Pet Grooming",
    "image": "https://groomieroomie.com/og-image.jpg",
    "url": "https://groomieroomie.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://groomieroomie.com/logo.png"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": Number(avgRating.toFixed(1)),
      "reviewCount": reviews.length,
      "bestRating": 5,
      "worstRating": 1
    },
    "review": reviews.map(r => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": r.author
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": 5
      },
      "reviewBody": r.reviewBody,
      "datePublished": r.datePublished
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// ARTICLE/BLOG SCHEMA
// ===============================
interface ArticleSchemaProps {
  headline: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image: string;
  url?: string;
}

export const ArticleSchema = ({ 
  headline, 
  description, 
  author, 
  datePublished, 
  dateModified, 
  image,
  url
}: ArticleSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": headline,
    "description": description,
    "image": image,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Groomie Roomie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://groomieroomie.com/logo.png"
      }
    },
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    ...(url && { "mainEntityOfPage": { "@type": "WebPage", "@id": url } })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// LOCATION-SPECIFIC SCHEMA (Enhanced for AI Search Engines)
// ===============================
interface LocationSchemaProps {
  locationName: string;
  neighborhood: string;
  city?: string;
  state?: string;
  zipCode?: string;
  geo: { lat: number; lng: number };
  description?: string;
  serviceAreas?: string[];
  slug?: string;
}

export const LocationSchema = ({
  locationName,
  neighborhood,
  city = "Denver",
  state = "CO",
  zipCode,
  geo,
  description,
  serviceAreas = [],
  slug
}: LocationSchemaProps) => {
  // Generate slug from neighborhood if not provided
  const locationSlug = slug || neighborhood.toLowerCase().replace(/\s+/g, '-');
  
  // Build areaServed with proper structure
  const areaServedStructured = serviceAreas.length > 0 
    ? serviceAreas.map(area => ({
        "@type": area === neighborhood ? "Neighborhood" : "City",
        "name": area,
        ...(area === neighborhood && { "containedInPlace": `${city}, ${state}` })
      }))
    : [{
        "@type": "Neighborhood",
        "name": neighborhood,
        "containedInPlace": `${city}, ${state}`
      }];

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "additionalType": [
      "https://www.productontology.org/id/Dog_grooming",
      "https://www.productontology.org/id/Pet_grooming"
    ],
    "name": `Groomie Roomie Mobile Pet Grooming - ${neighborhood}`,
    "alternateName": "Groomie Roomie",
    "description": description || `Premium mobile dog and cat grooming services in ${neighborhood}, ${city}. Luxury spa-level grooming delivered to your doorstep. No stressful car rides or kennel anxiety.`,
    "image": [
      "https://groomieroomie.com/og-image.jpg",
      "https://groomieroomie.com/logo.png"
    ],
    "logo": {
      "@type": "ImageObject",
      "url": "https://groomieroomie.com/logo.png"
    },
    "url": `https://groomieroomie.com/locations/${locationSlug}`,
    "telephone": "+1-720-243-2853",
    "email": "groomieroomie@gmail.com",
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Venmo", "Zelle"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2145 S Newport St",
      "addressLocality": "Denver",
      "addressRegion": "CO",
      "postalCode": "80224",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo.lat,
      "longitude": geo.lng
    },
    "areaServed": areaServedStructured,
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      },
      "geoRadius": "15 mi"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday", "Sunday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Mobile Pet Grooming Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Full Groom",
            "description": "Complete grooming with bath, haircut, nail trim, ear cleaning, and styling"
          },
          "priceRange": "$129-$175",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Bath & Brush",
            "description": "Gentle wash, brush-out, and blow dry"
          },
          "priceRange": "$99-$140",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cat Grooming",
            "description": "Specialized feline grooming including lion cuts and mat removal"
          },
          "priceRange": "$120-$210",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Deshedding Treatment",
            "description": "Professional deshedding to reduce shedding by up to 80%"
          },
          "price": "25",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Puppy Groom",
            "description": "Gentle first grooming experience for puppies 8 weeks and older"
          },
          "priceRange": "$60-$90",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Senior Pet Grooming",
            "description": "Arthritis-friendly gentle care for senior dogs and cats"
          },
          "priceRange": "$70-$115",
          "priceCurrency": "USD"
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "knowsAbout": [
      "Mobile Dog Grooming",
      "Mobile Cat Grooming",
      "Pet Grooming",
      "Dog Bathing",
      "Cat Bathing",
      "Nail Trimming",
      "De-shedding Treatment",
      "Teeth Cleaning",
      "Ear Cleaning",
      "Breed-Specific Grooming",
      `${neighborhood} Pet Services`
    ],
    "sameAs": [
      "https://www.instagram.com/groomie_roomie_denver_",
      "https://www.tiktok.com/@groomie_roomie_denver",
      "https://www.facebook.com/groomieroomie"
    ],
    "slogan": "Luxury Mobile Pet Grooming - We Come to You"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// PRICING TABLE SCHEMA
// ===============================
interface PricingTier {
  name: string;
  price: string;
  description: string;
}

interface PricingSchemaProps {
  serviceName: string;
  tiers: PricingTier[];
}

export const PricingSchema = ({ serviceName, tiers }: PricingSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": serviceName,
    "description": `${serviceName} pricing for mobile pet grooming in Denver`,
    "brand": {
      "@type": "Brand",
      "name": "Groomie Roomie"
    },
    "offers": tiers.map(tier => ({
      "@type": "Offer",
      "name": tier.name,
      "description": tier.description,
      "price": tier.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// HOWTO SCHEMA (for process pages)
// ===============================
interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
}

export const HowToSchema = ({ name, description, steps, totalTime }: HowToSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    ...(totalTime && { "totalTime": totalTime }),
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.image && { "image": step.image })
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// BREED LIST SCHEMA (for breed hub pages)
// ===============================
interface BreedListSchemaProps {
  breeds: Array<{ name: string; slug: string }>;
  species: 'dogs' | 'cats';
}

export const BreedListSchema = ({ breeds, species }: BreedListSchemaProps) => {
  const speciesLabel = species === 'dogs' ? 'Dog' : 'Cat';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${speciesLabel} Breed Grooming Guides`,
    "description": `Professional ${speciesLabel.toLowerCase()} breed grooming guides for Denver pet parents`,
    "numberOfItems": breeds.length,
    "itemListElement": breeds.map((breed, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "name": `${breed.name} Grooming Guide`,
        "description": `${breed.name} grooming tips, coat care, and recommended services for Denver.`,
        "url": `https://groomieroomie.com/breeds/${species}/${breed.slug}`
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// ===============================
// GENERIC SCHEMA COMPONENT
// ===============================
interface SchemaMarkupProps {
  schema: object;
}

const SchemaMarkup = ({ schema }: SchemaMarkupProps) => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;
